import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requireAuth?: boolean;
  allowedPermissions?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowedPermissions = [],
}) => {
  const { state } = useAuth();
  const location = useLocation();


  // Show loading while checking auth status
  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !state.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions
  if (allowedPermissions.length > 0 && state.user) {
    let userPermissions: string[] = [];

    try {
      const systemPerms = Array.isArray(state.user.permissions.system) ? state.user.permissions.system : [];
      const companyPerms = Array.isArray(state.user.permissions.company) ? state.user.permissions.company : [];
      const customPerms = state.user.permissions.custom ? Object.values(state.user.permissions.custom).flat() : [];

      userPermissions = [
        ...systemPerms,
        ...companyPerms,
        ...customPerms,
      ];
    } catch (error) {
      userPermissions = [];
    }

    const hasPermission = allowedPermissions.some(permission =>
      userPermissions.includes(permission)
    );

    if (!hasPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
};